'use client';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { usePathname } from 'next/navigation'; // Use next/navigation
import { CloseButton } from '@web/atoms';
import { useEnvVars } from '@web/app/contexts/EnvContext';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { ChatIcon } from './ChatIcon';
import {
  ButtonContainer,
  Button,
  UnreadMessageDot,
  ProactiveMessage,
  CloseButtonContainer,
} from './styles';
import { OVERLAPPING_PATH } from './constants';
import { userClickGladyChatButton } from './events';

export const ChatButtonApp = ({
  gladlySettings: { displayRules = { rules: [] } } = {},
}) => {
  const envVars = useEnvVars();
  const reporter = useReporter();
  const [isGladlyInitiated, setIsGladlyInitiated] = useState(false);
  const [showUnreadMessageDot, setShowUnreadMessageDot] = useState(false);
  const [campaignData, setCampaignData] = useState(null);
  const [showProactiveMessage, setShowProactiveMessage] = useState(false);
  const {
    isLoading: isCustomerLoading,
    isAuthenticated,
    customer,
  } = useSessionCustomer();

  // const router = useRouter();
  const pathname = usePathname(); // Get the current pathname

  const findOverlappingPath = path => {
    if (path === undefined) {
      return false;
    }
    const result = OVERLAPPING_PATH.find(
      overlapPath => path.indexOf(overlapPath) !== -1,
    );
    return result !== undefined;
  };

  const isOverlapping = findOverlappingPath(pathname);

  // Checks if the current URL is in the excluded list of Gladly settings page
  const isExcludedPath = displayRules?.rules.some(item =>
    pathname.includes(item.content.value),
  );

  useEffect(() => {
    const checkGladlyLoaded = () => {
      const { Gladly } = window;
      return Gladly && typeof Gladly.init === 'function';
    };

    const waitForGladly = setInterval(() => {
      if (checkGladlyLoaded()) {
        clearInterval(waitForGladly);

        // eslint-disable-next-line no-undef
        const routeChangeHandler = () => Gladly.navigate();
        // eslint-disable-next-line no-undef
        Gladly.init({
          autoShowButton: false,
          appId: envVars.GLADLY_PUBLISHABLE_KEY,
        })
          .then(function () {
            // eslint-disable-next-line no-undef
            Gladly.on('message:received', () => {
              setShowUnreadMessageDot(true);
            });
            // eslint-disable-next-line no-undef
            Gladly.on('campaign:triggered', e => {
              setShowUnreadMessageDot(true);
              setCampaignData(e[0]);
              setShowProactiveMessage(true);
            });
            // eslint-disable-next-line no-undef
            Gladly.on('sidekick:opened', e => {
              setShowUnreadMessageDot(false);
            });
            setIsGladlyInitiated(true);

            // Add event listener for pathname change
            const handlePathnameChange = () => {
              routeChangeHandler();
            };

            // Use React state or other hooks to detect changes in the pathname
            // For example, you can compare the current pathname with a previous one
            let prevPathname = pathname;
            const checkPathnameChange = () => {
              if (prevPathname !== pathname) {
                handlePathnameChange();
                prevPathname = pathname;
              }
            };

            const interval = setInterval(checkPathnameChange, 100); // Check for pathname changes every 100ms

            return () => clearInterval(interval); // Clean up the interval on component unmount
          })
          .catch(() => {});
      }
    }, 100); // try it each 100ms until Gladly is loaded
  }, [pathname, envVars.GLADLY_PUBLISHABLE_KEY]); // Depend on the pathname

  useEffect(() => {
    const { Gladly } = window; // Access the Gladly object from the global window

    const isGladlyAvailable = Gladly;
    if (
      !isCustomerLoading &&
      customer &&
      isAuthenticated &&
      isGladlyAvailable &&
      isGladlyInitiated
    ) {
      const { first_name, email } = customer;
      if (Gladly.clearUser && Gladly.setUser && first_name && email) {
        Gladly.clearUser();
        Gladly.setUser({
          name: first_name,
          email,
        });
      }
    }
  }, [customer, isAuthenticated, isCustomerLoading, isGladlyInitiated]);

  const onChatButtonHandler = () => {
    reporter.tag(userClickGladyChatButton({ path: pathname }));
    const { Gladly } = window;
    if (Gladly.show) {
      if (campaignData) {
        Gladly.applyCampaign(campaignData.id);
        setCampaignData(null);
      } else Gladly.show();
    }
  };

  const onCloseButtonClick = e => {
    e.stopPropagation();
    setShowProactiveMessage(false);
  };

  if (isExcludedPath || !isGladlyInitiated) {
    return <></>;
  }

  return (
    <ButtonContainer shown={true} isOverlapping={isOverlapping}>
      {showProactiveMessage && campaignData && (
        <>
          <ProactiveMessage
            className="campaign-message"
            onClick={onChatButtonHandler}
          >
            {campaignData.actions[0].content.message}
            <CloseButtonContainer onClick={onCloseButtonClick}>
              <CloseButton color="border.dark" />
            </CloseButtonContainer>
          </ProactiveMessage>
        </>
      )}
      <Button
        id={'custom-gladly-chat-button'}
        aria-label={
          'Click here to chat with a customer service representative.'
        }
        showUnreadMessageDot={showUnreadMessageDot}
        onClick={onChatButtonHandler}
      >
        <ChatIcon />
      </Button>

      {showUnreadMessageDot && <UnreadMessageDot />}
    </ButtonContainer>
  );
};

ChatButtonApp.displayName = 'ChatButtonApp';

ChatButtonApp.propTypes = {
  gladlySettings: PropTypes.object,
};
